// src/components/dashboard/UnitEdit/UnitInclusions/UnitInclusions.tsx
import React from 'react';
import { Formik, Form, Field, FormikState } from 'formik';
import { getIn } from 'formik';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import Input from '@/components/shared/Input';
import Button from '@/components/shared/Button';
import UICheckbox from '@/components/shared/UICheckbox';
import './UnitInclusions.scss';

export interface UnitInclusionsData {
  characteristics: {
    parking: {
      hasIndoor: boolean;
      hasOutdoor: boolean;
      indoorCount: number;
      outdoorCount: number;
      indoorSpaces: string;
      outdoorSpaces: string;
    };
    storage: {
      hasLockerOrStorage: boolean;
      storageDescription: string;
    };
    otherAccessories: {
      hasOtherAccessories: boolean;
      otherAccessoriesDescription: string;
    };
    furniture: {
      tables: { count: number; included: boolean };
      chairs: { count: number; included: boolean };
      armchairs: { count: number; included: boolean };
      chests: { count: number; included: boolean };
      couches: { count: number; included: boolean };
      beds: { count: number; included: boolean };
      appliances: {
        dryer: boolean;
        stove: boolean;
        washer: boolean;
        microwave: boolean;
        dishwasher: boolean;
        refrigerator: boolean;
      };
      isIncluded: boolean | null;
      hasOther: boolean;
      otherDescription: string;
    };
  };
}

export interface UnitInclusionsProps {
  /**
   * Дані секції
   */
  data: UnitInclusionsData;

  /**
   * Callback для збереження даних
   */
  onSave: (data: Partial<UnitInclusionsData>) => Promise<void>;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Обробник зміни стану згорнутості
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UnitInclusions';

// Default initial values for clearing the form
const defaultInitialValues: UnitInclusionsData = {
  characteristics: {
    parking: {
      hasIndoor: false,
      hasOutdoor: false,
      indoorCount: 0,
      outdoorCount: 0,
      indoorSpaces: '',
      outdoorSpaces: '',
    },
    storage: {
      hasLockerOrStorage: false,
      storageDescription: '',
    },
    otherAccessories: {
      hasOtherAccessories: false,
      otherAccessoriesDescription: '',
    },
    furniture: {
      tables: { count: 0, included: false },
      chairs: { count: 0, included: false },
      armchairs: { count: 0, included: false },
      chests: { count: 0, included: false },
      couches: { count: 0, included: false },
      beds: { count: 0, included: false },
      appliances: {
        dryer: false,
        stove: false,
        washer: false,
        microwave: false,
        dishwasher: false,
        refrigerator: false,
      },
      isIncluded: null,
      hasOther: false,
      otherDescription: '',
    },
  },
};
/**
 * Компонент для відображення та редагування включень юніта
 */
const UnitInclusions: React.FC<UnitInclusionsProps> = ({
  data,
  onSave,
  loading = false,
  collapsed,
  onToggleCollapse,
  className = '',
}) => {
  const handleSubmit = async (values: UnitInclusionsData) => {
    await onSave(values);
  };

  const handleClear = (
    resetForm: (nextState?: Partial<FormikState<UnitInclusionsData>>) => void,
  ) => {
    resetForm({ values: defaultInitialValues });
  };

  return (
    <UICollapsibleSection
      title="Inclusions"
      description="Description of leased dwelling, accessories and dependencies."
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading inclusions..."
      className={`${baseClass} ${className}`.trim()}
    >
      <Formik
        enableReinitialize={true}
        initialValues={data}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          handleBlur,
          handleChange,
          setFieldValue,
          resetForm,
        }) => (
          <Form className={`${baseClass}__form`}>
            {/* Parking section - 2 columns */}
            <div className={`${baseClass}__section-pair`}>
              {/* Outdoor parking */}
              <div className={`${baseClass}__section-column`}>
                <label className={`${baseClass}__checkbox-label`}>
                  <UICheckbox
                    name="characteristics.parking.hasOutdoor"
                    checked={values.characteristics.parking.hasOutdoor}
                    onChange={handleChange}
                  />
                  Outdoor parking
                </label>
                <div className={`${baseClass}__fields-row`}>
                  <div className={`${baseClass}__fields-column`}>
                    <div className={`${baseClass}__field-label`}>
                      Number of places
                    </div>
                    <div className={`${baseClass}__field-label`}>
                      Parking spaces
                    </div>
                  </div>
                  <div className={`${baseClass}__fields-column`}>
                    <Input
                      className={`${baseClass}__field-input`}
                      onChange={handleChange}
                      isDisabled={!values.characteristics.parking.hasOutdoor}
                      type="number"
                      onBlur={handleBlur}
                      value={values.characteristics.parking.outdoorCount}
                      name="characteristics.parking.outdoorCount"
                      errors={getIn(
                        errors,
                        'characteristics.parking.outdoorCount',
                      )}
                    />
                    <Input
                      className={`${baseClass}__field-input`}
                      onChange={handleChange}
                      isDisabled={!values.characteristics.parking.hasOutdoor}
                      type="text" // ← тепер text
                      onBlur={handleBlur}
                      placeholder="Describe parking spaces" // ← додати placeholder
                      value={values.characteristics.parking.outdoorSpaces}
                      name="characteristics.parking.outdoorSpaces"
                      errors={getIn(
                        errors,
                        'characteristics.parking.outdoorSpaces',
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Indoor parking */}
              <div className={`${baseClass}__section-column`}>
                <label className={`${baseClass}__checkbox-label`}>
                  <UICheckbox
                    name="characteristics.parking.hasIndoor"
                    checked={values.characteristics.parking.hasIndoor}
                    onChange={handleChange}
                  />
                  Indoor parking
                </label>
                <div className={`${baseClass}__fields-row`}>
                  <div className={`${baseClass}__fields-column`}>
                    <div className={`${baseClass}__field-label`}>
                      Number of places
                    </div>
                    <div className={`${baseClass}__field-label`}>
                      Parking spaces
                    </div>
                  </div>
                  <div className={`${baseClass}__fields-column`}>
                    <Input
                      className={`${baseClass}__field-input`}
                      onChange={handleChange}
                      isDisabled={!values.characteristics.parking.hasIndoor}
                      type="number"
                      onBlur={handleBlur}
                      value={values.characteristics.parking.indoorCount}
                      name="characteristics.parking.indoorCount"
                      errors={getIn(
                        errors,
                        'characteristics.parking.indoorCount',
                      )}
                    />
                    <Input
                      className={`${baseClass}__field-input`}
                      onChange={handleChange}
                      isDisabled={!values.characteristics.parking.hasIndoor}
                      type="text"
                      onBlur={handleBlur}
                      value={values.characteristics.parking.indoorSpaces}
                      name="characteristics.parking.indoorSpaces"
                      errors={getIn(
                        errors,
                        'characteristics.parking.indoorSpaces',
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Storage & Other accessories section - 2 columns */}
            <div className={`${baseClass}__section-pair`}>
              {/* Locker or storage space */}
              <div className={`${baseClass}__section-column`}>
                <label className={`${baseClass}__checkbox-label`}>
                  <UICheckbox
                    name="characteristics.storage.hasLockerOrStorage"
                    checked={values.characteristics.storage.hasLockerOrStorage}
                    onChange={handleChange}
                  />
                  Locker or storage space
                </label>
                <Field
                  className={`${baseClass}__textarea`}
                  as="textarea"
                  placeholder="Specify"
                  disabled={!values.characteristics.storage.hasLockerOrStorage}
                  name="characteristics.storage.storageDescription"
                  value={values.characteristics.storage.storageDescription}
                  onChange={handleChange}
                />
              </div>

              {/* Other accessories and dependencies */}
              <div className={`${baseClass}__section-column`}>
                <label className={`${baseClass}__checkbox-label`}>
                  <UICheckbox
                    name="characteristics.otherAccessories.hasOtherAccessories"
                    checked={
                      values.characteristics.otherAccessories
                        .hasOtherAccessories
                    }
                    onChange={handleChange}
                  />
                  Other accessories and dependencies
                </label>
                <Field
                  className={`${baseClass}__textarea`}
                  as="textarea"
                  placeholder="Specify"
                  disabled={
                    !values.characteristics.otherAccessories.hasOtherAccessories
                  }
                  name="characteristics.otherAccessories.otherAccessoriesDescription"
                  value={
                    values.characteristics.otherAccessories
                      .otherAccessoriesDescription
                  }
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Furniture is leased - radio buttons */}
            <div className={`${baseClass}__furniture-leased`}>
              <span className={`${baseClass}__furniture-leased-label`}>
                Furniture is leased and included in the rent:
              </span>
              <label className={`${baseClass}__radio-label`}>
                <Field
                  className={`${baseClass}__radio`}
                  type="radio"
                  name="characteristics.furniture.isIncluded"
                  value="true"
                  checked={values.characteristics.furniture.isIncluded === true}
                  onChange={() =>
                    setFieldValue('characteristics.furniture.isIncluded', true)
                  }
                />
                Yes
              </label>
              <label className={`${baseClass}__radio-label`}>
                <Field
                  className={`${baseClass}__radio`}
                  type="radio"
                  name="characteristics.furniture.isIncluded"
                  value="false"
                  checked={
                    values.characteristics.furniture.isIncluded === false
                  }
                  onChange={() =>
                    setFieldValue('characteristics.furniture.isIncluded', false)
                  }
                />
                No
              </label>
            </div>

            {/* Appliances section */}
            <div className={`${baseClass}__appliances`}>
              <div className={`${baseClass}__section-title`}>Appliances</div>
              <div className={`${baseClass}__appliances-grid`}>
                <div className={`${baseClass}__appliances-column`}>
                  <label className={`${baseClass}__checkbox-label`}>
                    <UICheckbox
                      name="characteristics.furniture.appliances.stove"
                      checked={
                        values.characteristics.furniture.appliances.stove
                      }
                      onChange={handleChange}
                    />
                    Stove
                  </label>
                  <label className={`${baseClass}__checkbox-label`}>
                    <UICheckbox
                      name="characteristics.furniture.appliances.microwave"
                      checked={
                        values.characteristics.furniture.appliances.microwave
                      }
                      onChange={handleChange}
                    />
                    Microwave oven
                  </label>
                </div>

                <div className={`${baseClass}__appliances-column`}>
                  <label className={`${baseClass}__checkbox-label`}>
                    <UICheckbox
                      name="characteristics.furniture.appliances.dishwasher"
                      checked={
                        values.characteristics.furniture.appliances.dishwasher
                      }
                      onChange={handleChange}
                    />
                    Dishwasher
                  </label>
                  <label className={`${baseClass}__checkbox-label`}>
                    <UICheckbox
                      name="characteristics.furniture.appliances.refrigerator"
                      checked={
                        values.characteristics.furniture.appliances.refrigerator
                      }
                      onChange={handleChange}
                    />
                    Refrigerator
                  </label>
                </div>

                <div className={`${baseClass}__appliances-column`}>
                  <label className={`${baseClass}__checkbox-label`}>
                    <UICheckbox
                      name="characteristics.furniture.appliances.washer"
                      checked={
                        values.characteristics.furniture.appliances.washer
                      }
                      onChange={handleChange}
                    />
                    Washer
                  </label>
                  <label className={`${baseClass}__checkbox-label`}>
                    <UICheckbox
                      name="characteristics.furniture.appliances.dryer"
                      checked={
                        values.characteristics.furniture.appliances.dryer
                      }
                      onChange={handleChange}
                    />
                    Dryer
                  </label>
                </div>
              </div>
            </div>
            {/* Furniture section */}
            <div className={`${baseClass}__furniture`}>
              <div className={`${baseClass}__section-title`}>Furniture</div>
              <div className={`${baseClass}__furniture-grid`}>
                {/* Left column */}
                <div className={`${baseClass}__furniture-column`}>
                  <div className={`${baseClass}__furniture-row`}>
                    <div className={`${baseClass}__furniture-labels`}>
                      <label className={`${baseClass}__checkbox-label`}>
                        <UICheckbox
                          name="characteristics.furniture.tables.included"
                          checked={
                            values.characteristics.furniture.tables.included
                          }
                          onChange={handleChange}
                        />
                        Table(s)
                      </label>
                      <label className={`${baseClass}__checkbox-label`}>
                        <UICheckbox
                          name="characteristics.furniture.chairs.included"
                          checked={
                            values.characteristics.furniture.chairs.included
                          }
                          onChange={handleChange}
                        />
                        Chair(s)
                      </label>
                      <label className={`${baseClass}__checkbox-label`}>
                        <UICheckbox
                          name="characteristics.furniture.armchairs.included"
                          checked={
                            values.characteristics.furniture.armchairs.included
                          }
                          onChange={handleChange}
                        />
                        Armchair(s)
                      </label>
                    </div>
                    <div className={`${baseClass}__furniture-inputs`}>
                      <Input
                        className={`${baseClass}__furniture-input`}
                        onChange={handleChange}
                        type="number"
                        onBlur={handleBlur}
                        placeholder="Number"
                        value={values.characteristics.furniture.tables.count}
                        name="characteristics.furniture.tables.count"
                        isDisabled={
                          !values.characteristics.furniture.tables.included
                        }
                        errors=""
                      />
                      <Input
                        className={`${baseClass}__furniture-input`}
                        onChange={handleChange}
                        type="number"
                        onBlur={handleBlur}
                        placeholder="Number"
                        value={values.characteristics.furniture.chairs.count}
                        name="characteristics.furniture.chairs.count"
                        isDisabled={
                          !values.characteristics.furniture.chairs.included
                        }
                        errors=""
                      />
                      <Input
                        className={`${baseClass}__furniture-input`}
                        onChange={handleChange}
                        type="number"
                        onBlur={handleBlur}
                        placeholder="Number"
                        value={values.characteristics.furniture.armchairs.count}
                        name="characteristics.furniture.armchairs.count"
                        isDisabled={
                          !values.characteristics.furniture.armchairs.included
                        }
                        errors=""
                      />
                    </div>
                  </div>
                </div>

                {/* Right column */}
                <div className={`${baseClass}__furniture-column`}>
                  <div className={`${baseClass}__furniture-row`}>
                    <div className={`${baseClass}__furniture-labels`}>
                      <label className={`${baseClass}__checkbox-label`}>
                        <UICheckbox
                          name="characteristics.furniture.chests.included"
                          checked={
                            values.characteristics.furniture.chests.included
                          }
                          onChange={handleChange}
                        />
                        Chest(s) of drawers
                      </label>
                      <label className={`${baseClass}__checkbox-label`}>
                        <UICheckbox
                          name="characteristics.furniture.couches.included"
                          checked={
                            values.characteristics.furniture.couches.included
                          }
                          onChange={handleChange}
                        />
                        Couch(es)
                      </label>
                      <label className={`${baseClass}__checkbox-label`}>
                        <UICheckbox
                          name="characteristics.furniture.beds.included"
                          checked={
                            values.characteristics.furniture.beds.included
                          }
                          onChange={handleChange}
                        />
                        Bed(s)
                      </label>
                    </div>
                    <div className={`${baseClass}__furniture-inputs`}>
                      <Input
                        className={`${baseClass}__furniture-input`}
                        onChange={handleChange}
                        type="number"
                        onBlur={handleBlur}
                        placeholder="Number"
                        value={values.characteristics.furniture.chests.count}
                        name="characteristics.furniture.chests.count"
                        isDisabled={
                          !values.characteristics.furniture.chests.included
                        }
                        errors=""
                      />
                      <Input
                        className={`${baseClass}__furniture-input`}
                        onChange={handleChange}
                        type="number"
                        onBlur={handleBlur}
                        placeholder="Number"
                        value={values.characteristics.furniture.couches.count}
                        name="characteristics.furniture.couches.count"
                        isDisabled={
                          !values.characteristics.furniture.couches.included
                        }
                        errors=""
                      />
                      <Input
                        className={`${baseClass}__furniture-input`}
                        onChange={handleChange}
                        type="number"
                        onBlur={handleBlur}
                        placeholder="Number"
                        value={values.characteristics.furniture.beds.count}
                        name="characteristics.furniture.beds.count"
                        isDisabled={
                          !values.characteristics.furniture.beds.included
                        }
                        errors=""
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Other section */}
            <div className={`${baseClass}__other`}>
              <label className={`${baseClass}__checkbox-label`}>
                <UICheckbox
                  name="characteristics.furniture.hasOther"
                  checked={values.characteristics.furniture.hasOther}
                  onChange={handleChange}
                />
                Other
              </label>
              <Field
                className={`${baseClass}__textarea`}
                as="textarea"
                placeholder="Specify"
                disabled={!values.characteristics.furniture.hasOther}
                name="characteristics.furniture.otherDescription"
                value={values.characteristics.furniture.otherDescription}
                onChange={handleChange}
              />
            </div>

            {/* Action buttons */}
            <div className={`${baseClass}__actions`}>
              <Button type="submit" theme="primary">
                Save
              </Button>
              <Button
                type="button"
                theme="secondary"
                onClick={() => handleClear(resetForm)}
              >
                Clear
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </UICollapsibleSection>
  );
};

export default UnitInclusions;
